/**
 * Reusable format functions for display across pages/components.
 * Import only what you need: import { formatFullName, formatPhone } from '../utils/formatters';
 */

const EMPTY_PLACEHOLDER = "—";

/**
 * Unwrap backend strings like "\"suresh\"" to "suresh" (removes outer quotes).
 * @param {*} v - Value (string, number, etc.)
 * @returns {string|null} Unwrapped string or original value
 */
export const unwrapString = (v) => {
  if (v == null) return v;
  const s = String(v).trim();
  if (s.length >= 2 && s[0] === '"' && s[s.length - 1] === '"') {
    return s.slice(1, -1).replace(/\\"/g, '"');
  }
  return s;
};

/**
 * Pascal/title case: capitalize first letter of each word
 * @param {string} val - Input value
 * @returns {string} Formatted string or original if empty
 */
export const formatPascal = (val) => {
  if (!val || val === EMPTY_PLACEHOLDER) return val;
  return String(val)
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
};

/**
 * Full name: First Name, Last Name with Pascal case each (e.g. "John, Doe")
 * @param {string} nameStr - Combined "FirstName LastName" string
 * @param {string} [firstName] - Optional first name (if available separately)
 * @param {string} [lastName] - Optional last name (if available separately)
 * @returns {string} Formatted "FirstName, LastName"
 */
export const formatFullName = (nameStr, firstName, lastName) => {
  if (firstName != null || lastName != null) {
    const fn = formatPascal(String(firstName || "").trim());
    const ln = formatPascal(String(lastName || "").trim());
    const result = [fn, ln].filter(Boolean).join(", ");
    return result || EMPTY_PLACEHOLDER;
  }
  if (!nameStr || nameStr === EMPTY_PLACEHOLDER) return nameStr;
  const parts = String(nameStr).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return EMPTY_PLACEHOLDER;
  if (parts.length === 1) return formatPascal(parts[0]);
  const fname = formatPascal(parts[0]);
  const lname = formatPascal(parts.slice(1).join(" "));
  return `${fname}, ${lname}`;
};

/**
 * Phone format
 * - With country code: +91 923-546-7257
 * - Without code:       923-546-7257
 * @param {string} val - Phone number (digits or mixed)
 * @returns {string} Formatted phone or original if < 10 digits
 */
export const formatPhone = (val) => {
  if (!val || val === EMPTY_PLACEHOLDER) return val;
  const raw = String(val).trim();

  // Strip all non-digits for consistent parsing
  const allDigits = raw.replace(/\D/g, "");
  if (!allDigits) return raw;

  // Last 10 digits are treated as the local number, any leading digits are country code
  const localDigits = allDigits.slice(-10);
  const countryDigits = allDigits.length > 10 ? allDigits.slice(0, allDigits.length - 10) : "";

  let local = "";
  if (localDigits.length <= 3) {
    local = localDigits;
  } else if (localDigits.length <= 6) {
    local = `${localDigits.slice(0, 3)}-${localDigits.slice(3)}`;
  } else {
    // 7–10 digits: 3-3-rest (e.g. 976-647-67554)
    local = `${localDigits.slice(0, 3)}-${localDigits.slice(3, 6)}-${localDigits.slice(6)}`;
  }

  const countryCode = countryDigits ? `+${countryDigits}` : "";
  return countryCode ? `${countryCode} ${local}` : local;
};

/**
 * Email: always lowercase for display
 * @param {string} val - Email address
 * @returns {string} Lowercase email
 */
export const formatEmail = (val) => {
  if (!val || val === EMPTY_PLACEHOLDER) return val;
  return String(val).trim().toLowerCase();
};

/**
 * Map a service full name to a short code (e.g. "Search Engine Optimization" -> "SEO").
 * Falls back to an acronym built from the first letter of each word.
 * @param {string} name - Service display name from API / DB
 * @returns {string} Short code for compact table display
 */
export const formatServiceShortcode = (name) => {
  if (!name || name === EMPTY_PLACEHOLDER) return name;
  const raw = String(name).trim();
  if (!raw) return raw;

  const lower = raw.toLowerCase();

  // Explicit mappings for common services
  const map = {
    "search engine optimization": "SEO",
    "seo": "SEO",
    "search engine marketing": "SEM",
    "sem": "SEM",
    "social media marketing": "SMM",
    "smm": "SMM",
    "social media optimization": "SMO",
    "smo": "SMO",
    "web development": "WEB",
    "website development": "WEB",
    "web design": "WEB",
    "web application development": "WAP",
    "wap": "WAP"
  };

  if (map[lower]) return map[lower];

  // Heuristic fallbacks based on keywords
  if (lower.includes("search engine") && lower.includes("optimization")) return "SEO";
  if (lower.includes("search engine") && lower.includes("marketing")) return "SEM";
  if (lower.includes("social media") && lower.includes("marketing")) return "SMM";
  if (lower.includes("social media") && lower.includes("optimization")) return "SMO";
  if (lower.includes("web application")) return "WAP";
  if (lower.includes("web")) return "WEB";

  // Generic acronym from first letters of words
  const acronym = raw
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase())
    .join("");

  return acronym || raw;
};

/**
 * Look up a service short code from the backend `/service/categories` structure.
 * Falls back to the original `serviceName` when no explicit code is found.
 *
 * This is used so that table columns display the SAME short code that the
 * backend defines (e.g. "BANNER") instead of a best‑guess acronym ("B").
 *
 * @param {string} serviceName - Display name coming from lead/customer record
 * @param {Array} servicesData - Full categories array from `/service/categories`
 * @returns {string} Backend-defined shortcode or the original name
 */
export const getServiceShortcodeFromCategories = (serviceName, servicesData) => {
  if (!serviceName || serviceName === EMPTY_PLACEHOLDER) return serviceName;
  if (!Array.isArray(servicesData) || servicesData.length === 0) {
    return serviceName;
  }

  const normalizedServiceName = String(serviceName).trim().toLowerCase();
  if (!normalizedServiceName) return serviceName;

  for (const category of servicesData) {
    const subs = Array.isArray(category?.subcategories)
      ? category.subcategories
      : [];

    const subcategory = subs.find((sub) => {
      const subName =
        sub.subcategory_name ||
        sub.Subcategory_Name ||
        sub.name ||
        "";
      return subName.trim().toLowerCase() === normalizedServiceName;
    });

    if (subcategory) {
      const code =
        subcategory.Subcategory_Code ||
        subcategory.subcategory_code ||
        subcategory.Subcategory_Shortcode ||
        subcategory.subcategory_shortcode ||
        subcategory.Shortcode ||
        subcategory.shortcode ||
        subcategory.Service_Code ||
        subcategory.service_code ||
        subcategory.Code ||
        subcategory.code;

      if (code && String(code).trim() !== "") {
        return String(code).trim();
      }

      // No explicit code field – fall back to name as given by backend
      return serviceName;
    }
  }

  // No match – return the original so we never invent a wrong code
  return serviceName;
};

/**
 * Normalize `/clients` list item for dropdowns (keeps type + name fields for {@link getCustomerSelectLabel}).
 * @param {object} item - API row `{ client }` or flat client
 * @returns {{ id: *, Customer_Type?: *, customer_type?: *, First_Name?: *, Last_Name?: *, Company_Name?: *, name: string }|null}
 */
export function mapCustomerListItemFromApi(item) {
  const c = item?.client || item;
  const id = c?.client_id;
  if (id == null || id === "") return null;
  const personName = `${c.First_Name || c.first_name || ""} ${c.Last_Name || c.last_name || ""}`.trim();
  return {
    id,
    Customer_Type: c.Customer_Type,
    customer_type: c.customer_type,
    First_Name: c.First_Name,
    first_name: c.first_name,
    Last_Name: c.Last_Name,
    last_name: c.last_name,
    Company_Name: c.Company_Name,
    company_name: c.company_name,
    companyName: c.companyName,
    name:
      personName ||
      c.Customer_Name ||
      c.customer_name ||
      c.Company_Name ||
      c.company_name ||
      "—"
  };
}

/**
 * Label for customer dropdowns: Organization → company name; Individual (or unknown) → person name.
 * @param {object} c - Row from {@link mapCustomerListItemFromApi} or API customer object
 * @returns {string}
 */
export function getCustomerSelectLabel(c) {
  if (c == null) return "—";
  const typeRaw = String(c.Customer_Type || c.customer_type || "").trim().toLowerCase();
  const isOrg =
    typeRaw === "organization" ||
    typeRaw === "organisation" ||
    typeRaw === "org";

  const company = String(c.Company_Name || c.company_name || c.companyName || "").trim();
  const first = String(c.First_Name || c.first_name || "").trim();
  const last = String(c.Last_Name || c.last_name || "").trim();
  const person = [first, last].filter(Boolean).join(" ").trim();
  const legacyName = String(c.name || c.Customer_Name || c.customer_name || "").trim();

  if (isOrg) {
    if (company) return company;
    return person || legacyName || "—";
  }
  if (person) return person;
  if (legacyName) return legacyName;
  if (company) return company;
  return "—";
}

/**
 * Lowercase string for filtering customer dropdowns (matches company, person name, and display label).
 * @param {object} c
 * @returns {string}
 */
export function getCustomerSelectSearchText(c) {
  if (c == null) return "";
  const label = getCustomerSelectLabel(c);
  const person = `${c.First_Name || c.first_name || ""} ${c.Last_Name || c.last_name || ""}`.trim();
  const company = String(c.Company_Name || c.company_name || c.companyName || "").trim();
  const legacy = String(c.name || "").trim();
  return [label, person, company, legacy].filter(Boolean).join(" ").toLowerCase();
}
